import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthApiError, connectedProviders, currentAccount, linkTelegram, signout, socialProviders } from '@/lib/auth/api';
import { createTelegramLogin } from '@/lib/auth/social';
import type { AccountIdentity } from '@/lib/auth/api';

export default function AccountPage() {
  const navigate = useNavigate();
  const [account, setAccount] = useState<AccountIdentity | null>(null);
  const [error, setError] = useState('');
  const [signingOut, setSigningOut] = useState(false);
  const [connected, setConnected] = useState<string[]>([]);
  const [canConnectTelegram, setCanConnectTelegram] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const telegramLogin = useRef<ReturnType<typeof createTelegramLogin> | null>(null);

  useEffect(() => {
    let active = true;
    currentAccount().then((identity) => {
      if (active) setAccount(identity);
    }).catch((failure: unknown) => {
      if (!active) return;
      if (failure instanceof AuthApiError && failure.status === 401) {
        navigate('/login', { replace: true });
      } else {
        setError(failure instanceof Error ? failure.message : 'Could not load account.');
      }
    });
    return () => { active = false; };
  }, [navigate]);

  // Optional section: if this fails, the rest of the account page still works.
  useEffect(() => {
    let active = true;
    Promise.all([connectedProviders(), socialProviders()]).then(async ([linked, providers]) => {
      if (!active) return;
      setConnected(linked);
      if (!providers.telegram || linked.includes('telegram')) return;
      const login = createTelegramLogin(providers.telegram.clientId);
      await login.prepare();
      if (!active) return;
      telegramLogin.current = login;
      setCanConnectTelegram(true);
    }).catch(() => undefined);
    return () => { active = false; };
  }, []);

  async function handleConnectTelegram() {
    const login = telegramLogin.current;
    if (!login) return;
    setError('');
    try {
      const idToken = await login.open();
      if (!idToken) return;
      setConnecting(true);
      await linkTelegram(idToken);
      setConnected((current) => [...current, 'telegram']);
      setCanConnectTelegram(false);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Could not connect Telegram.');
    } finally {
      setConnecting(false);
      if (telegramLogin.current) void login.prepare().catch(() => undefined);
    }
  }

  async function handleSignout() {
    setSigningOut(true);
    setError('');
    try {
      await signout();
      navigate('/login', { replace: true });
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Signout failed. Try again.');
    } finally { setSigningOut(false); }
  }

  return (
    <main className="min-h-screen overflow-x-hidden bg-background-50 text-foreground-950 px-4 py-12">
      <section className="mx-auto flex max-w-xl flex-col gap-6 border border-background-200 p-6">
        <h1 className="font-heading text-2xl font-bold">Your account / គណនីរបស់អ្នក</h1>
        {error && <p role="alert" className="text-secondary-700">{error}</p>}
        {!account && !error && <p role="status">Loading account…</p>}
        {account && <>
          <dl className="grid grid-cols-[auto_1fr] gap-4 text-sm">
            <dt>Email</dt><dd className="break-all">{account.email}</dd>
            <dt>Email status</dt><dd>Verified / បានផ្ទៀងផ្ទាត់</dd>
            <dt>Phone</dt><dd>{account.phone} (not verified)</dd>
            <dt>Organization roles</dt><dd>{account.memberships.map((membership) => membership.role).join(', ') || 'None'}</dd>
            <dt>Google</dt><dd>{connected.includes('google') ? 'Connected' : 'Not connected'}</dd>
            <dt>Telegram</dt><dd>{connected.includes('telegram') ? 'Connected' : 'Not connected'}</dd>
          </dl>
          <p className="text-sm text-foreground-600">
            Company/licence submission is not connected yet. Your account does not mean the company or its ads are approved.
          </p>
          {canConnectTelegram && !connected.includes('telegram') && (
            <button type="button" disabled={connecting} onClick={handleConnectTelegram}
              className="min-h-11 border border-background-200 px-4 py-3 font-semibold cursor-pointer disabled:opacity-50 focus-visible:ring-2">
              {connecting ? 'Connecting…' : 'Connect Telegram'}
            </button>
          )}
          <button type="button" disabled={signingOut} onClick={handleSignout}
            className="min-h-11 bg-primary-500 px-4 py-3 font-semibold text-background-50 cursor-pointer disabled:opacity-50 focus-visible:ring-2">
            {signingOut ? 'Signing out…' : 'Sign out / ចាកចេញ'}
          </button>
        </>}
        <Link to="/" className="flex min-h-11 items-center text-primary-600 underline">Back to home</Link>
      </section>
    </main>
  );
}
